/**
 * Shared markdown renderer for article bodies. Used by Classic and Hero
 * (which render the whole body as one block). Long-form and Briefing split
 * body by chapters/points and use `articleMarkdownComponents` directly.
 *
 * The first paragraph of the body gets `.article-lede-p` for the drop-cap.
 * Pass `skipLede={true}` when an upstream component owns the lede (e.g.
 * essay preamble that comes before the first chapter).
 */
"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkUnwrapImages from "remark-unwrap-images";
import { useMemo } from "react";
import { articleMarkdownComponents } from "./article-markdown";

function splitFirstBlock(markdown: string): { first: string; rest: string } {
  const trimmed = markdown.trim();
  if (!trimmed) return { first: "", rest: "" };
  const blocks = trimmed.split(/\n\s*\n/);
  return { first: blocks[0] ?? "", rest: blocks.slice(1).join("\n\n") };
}

type Props = {
  body: string;
  /** When true, the first paragraph does NOT receive .article-lede-p. */
  skipLede?: boolean;
  /** Optional CSS class on the wrapper. Defaults to ".article-body". */
  className?: string;
};

export function ArticleBody({ body, skipLede = false, className = "article-body" }: Props) {
  const { first, rest } = useMemo(() => splitFirstBlock(body), [body]);
  const plainComponents = useMemo(() => articleMarkdownComponents(), []);
  const ledeComponents = useMemo(
    () =>
      articleMarkdownComponents({
        pTransform: (children) => <p className="article-lede-p">{children}</p>,
      }),
    [],
  );

  return (
    <div className={className}>
      {!skipLede && first ? (
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkUnwrapImages]} components={ledeComponents}>
          {first}
        </ReactMarkdown>
      ) : null}
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkUnwrapImages]} components={plainComponents}>
        {skipLede ? body : rest}
      </ReactMarkdown>
    </div>
  );
}
